'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'next-intl';
import { createLogger } from '@/shared/lib/logger';
import { useDrawingStore } from '@/features/drawing/model';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/shadcn/alert-dialog';
import { ControlEditor, type BackdropOption } from './ControlEditor';

const logger = createLogger('ControlEditorModal');

interface ControlEditorModalProps {
  isOpen: boolean;
  // null = open with no backdrop (clean schema).
  angleImageUrl: string | null;
  initialControlUrl?: string;
  onSave: (blob: Blob) => void;
  onCancel: () => void;
  // Optional backdrop picker (Brief sketch flow).
  backdropOptions?: BackdropOption[];
  selectedBackdropAngleId?: string | null;
  onBackdropChange?: (angleId: string | null) => void;
}

export function ControlEditorModal({
  isOpen,
  angleImageUrl,
  initialControlUrl,
  onSave,
  onCancel,
  backdropOptions,
  selectedBackdropAngleId,
  onBackdropChange,
}: ControlEditorModalProps) {
  const t = useTranslations('control.editor.discardChanges');
  const tCommon = useTranslations('common');
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [key, setKey] = useState(0);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  // Guard user-initiated dismissal (X button + backdrop click) against losing
  // unsaved drawing progress. The drawing store is read imperatively so the
  // modal does not re-render on every canvas stroke.
  const handleCancelRequest = useCallback(() => {
    const hasChanges = useDrawingStore.getState().canvas.hasChanges;
    if (hasChanges) {
      setShowDiscardDialog(true);
    } else {
      onCancel();
    }
  }, [onCancel]);

  const handleDiscardConfirm = useCallback(() => {
    setShowDiscardDialog(false);
    onCancel();
  }, [onCancel]);

  // Latest onCancel without re-running the history guard on every parent render.
  const onCancelRef = useRef(onCancel);
  useEffect(() => {
    onCancelRef.current = onCancel;
  }, [onCancel]);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Guard the browser Back button (plus hard reload / tab close) against losing
  // unsaved drawing progress. The editor is a fullscreen modal, not a route, so
  // Back would otherwise navigate the whole app away with no prompt. We push one
  // sentinel history entry on open; Back pops it and we decide here — same rule
  // as the X-button / backdrop guard. `beforeunload` covers reload / tab close.
  useEffect(() => {
    if (!isOpen) return undefined;

    // Sentinel so the next Back targets the editor, not the underlying page.
    // Clone existing state so Next.js App Router's history state survives.
    window.history.pushState({ ...window.history.state }, '');
    let sentinelActive = true;

    const handlePopState = () => {
      // Back consumed our sentinel.
      sentinelActive = false;
      if (useDrawingStore.getState().canvas.hasChanges) {
        // Re-arm the sentinel so the editor stays put, then prompt.
        window.history.pushState({ ...window.history.state }, '');
        sentinelActive = true;
        setShowDiscardDialog(true);
      } else {
        onCancelRef.current();
      }
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (useDrawingStore.getState().canvas.hasChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Closed via UI (X / backdrop / Save / discard): the sentinel is still in
      // history, so pop it to keep the back stack clean. When Back closed the
      // editor the sentinel was already consumed, so skip (avoids phantom nav).
      if (sentinelActive) {
        window.history.back();
      }
    };
  }, [isOpen]);

  // Handle window resize events (but NOT mobile keyboard/viewport changes)
  useEffect(() => {
    if (!isOpen) return;

    let lastWidth = window.innerWidth;
    let lastHeight = window.innerHeight;

    const handleResize = () => {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;

      // On mobile, keyboard open/close changes height but not width
      // Only re-mount if BOTH width AND height changed (actual window resize, orientation change)
      // If only height changed, it's likely keyboard - skip re-mount
      const widthChanged = Math.abs(newWidth - lastWidth) > 10;
      const heightChanged = Math.abs(newHeight - lastHeight) > 10;

      if (widthChanged && heightChanged) {
        logger.info('Window resize detected, re-mounting', {
          oldSize: { width: lastWidth, height: lastHeight },
          newSize: { width: newWidth, height: newHeight },
        });
        setKey((prev) => prev + 1);
        lastWidth = newWidth;
        lastHeight = newHeight;
      } else {
        logger.info('Skipping re-mount - likely keyboard', {
          widthChanged,
          heightChanged,
          oldSize: { width: lastWidth, height: lastHeight },
          newSize: { width: newWidth, height: newHeight },
        });
      }
    };

    // Debounce resize events
    let resizeTimer: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(handleResize, 200);
    };

    window.addEventListener('resize', debouncedResize);

    // REMOVED: visualViewport listeners - they cause re-mounts on mobile keyboard events
    // The ControlEditor component handles its own viewport changes without re-mounting

    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(resizeTimer);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      // Save original styles
      const originalBodyOverflow = document.body.style.overflow;
      const originalBodyBackground = document.body.style.backgroundColor;

      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      // Set black background for immersive experience
      document.body.style.backgroundColor = 'black';

      // Change theme-color meta tag to black for browser UI
      const themeColorMeta = document.querySelector('meta[name="theme-color"]');
      const originalThemeColor = themeColorMeta?.getAttribute('content') || '';
      if (themeColorMeta) {
        themeColorMeta.setAttribute('content', '#000000');
      }

      // Small delay for animation
      const timer = setTimeout(() => {
        setVisible(true);
      }, 10);

      return () => {
        clearTimeout(timer);
        // Restore original styles
        document.body.style.overflow = originalBodyOverflow;
        document.body.style.backgroundColor = originalBodyBackground;

        // Restore theme-color meta tag
        if (themeColorMeta && originalThemeColor) {
          themeColorMeta.setAttribute('content', originalThemeColor);
        }
      };
    } else {
      setVisible(false);
    }

    return undefined;
  }, [isOpen]);

  // Disable focus on drawer when modal is open (safer than disabling all elements)
  useEffect(() => {
    if (!isOpen || !visible) return undefined;

    // Only disable the drawer, not all body children
    const drawer = document.querySelector('[role="dialog"]');

    if (drawer && drawer instanceof HTMLElement) {
      logger.info('Setting drawer inert=true');
      drawer.inert = true;

      return () => {
        logger.info('Setting drawer inert=false');
        drawer.inert = false;
      };
    }
    return undefined;
  }, [isOpen, visible]);

  if (!mounted || !isOpen) {
    return null;
  }

  // Create portal to render outside of current DOM hierarchy
  // Wrapper div provides animation and ensures proper z-index
  return createPortal(
    <div
      key={key} // Force re-render on resize
      className="fixed inset-0 transition-opacity duration-200 bg-black"
      style={{
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        zIndex: 100000, // Higher than drawer's z-50
        // Ensure the container always fills the viewport
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        minHeight: '-webkit-fill-available',
      }}
    >
      {/* Backdrop to block interaction with background elements */}
      <div
        className="absolute inset-0 bg-black"
        style={{
          zIndex: 1,
          pointerEvents: 'auto', // Block all clicks to background
        }}
        onClick={handleCancelRequest} // Click outside to close (guarded)
      />

      {/* Editor on top of backdrop */}
      <div
        className="relative"
        style={{
          zIndex: 2,
          width: '100%',
          height: '100%',
        }}
      >
        <ControlEditor
          angleImageUrl={angleImageUrl}
          initialControlUrl={initialControlUrl}
          onSave={onSave}
          onCancel={handleCancelRequest}
          backdropOptions={backdropOptions}
          selectedBackdropAngleId={selectedBackdropAngleId}
          onBackdropChange={onBackdropChange}
        />
      </div>

      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        {/* The editor portal sits at z-index 100000; the dialog renders into its own
            body-level portal and must clear that, or it opens hidden behind the editor
            and the X button appears to do nothing. */}
        <AlertDialogContent
          className="z-[100001]"
          overlayClassName="z-[100001]"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{t('title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('message')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDiscardConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('discard')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>,
    document.body
  );
}
